import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, Download, Pencil, Trash2, FileText, Check, X as XIcon, ChevronLeft, ChevronRight, Power, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api, { getFileUrl } from '../../lib/api';
import MemberFieldManager from './MemberFieldManager';
import DocumentManager from './DocumentManager';

const inputCls = 'px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]';
const bool = (v) => v === true || v === 'true' || v === 'True';

const DOC_STATUS = {
  pending: { label: 'İnceleniyor', cls: 'bg-amber-500/10 text-amber-600 dark:text-amber-400', icon: Clock },
  approved: { label: 'Onaylandı', cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400', icon: CheckCircle2 },
  rejected: { label: 'Reddedildi', cls: 'bg-destructive/10 text-destructive', icon: XIcon },
  re_requested: { label: 'Yeniden İstendi', cls: 'bg-destructive/10 text-destructive', icon: AlertCircle },
};

export default function MemberManager() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 10, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', is_active: '', verified: '', doc_status: '', date_from: '', date_to: '', page_size: 10 });
  const [page, setPage] = useState(1);
  const [fields, setFields] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [docsMember, setDocsMember] = useState(null);
  const [docs, setDocs] = useState([]);
  const [reviewNotes, setReviewNotes] = useState({});
  const [mainTab, setMainTab] = useState('members');
  const [documentsEnabled, setDocumentsEnabled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tab') === 'documents') setMainTab('documents');
  }, [location.search]);

  const fetchMembers = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: filters.page_size, search: filters.search, is_active: filters.is_active, verified: filters.verified, doc_status: filters.doc_status, date_from: filters.date_from, date_to: filters.date_to };
    api.get('/admin/members', { params }).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [page, filters]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);
  useEffect(() => { api.get('/admin/member-fields').then(r => setFields(r.data.filter(f => bool(f.is_enabled)))).catch(() => {}); }, []);
  useEffect(() => { api.get('/membership/status').then(r => setDocumentsEnabled(!!r.data.documents_enabled)).catch(() => {}); }, []);

  const applyFilter = (k, v) => { setPage(1); setFilters(f => ({ ...f, [k]: v })); };

  const toggleActive = async (m) => {
    const action = bool(m.is_active) ? 'deactivate' : 'activate';
    try { await api.post(`/admin/members/${m.id}/${action}`); toast.success(bool(m.is_active) ? 'Üye pasifleştirildi' : 'Üye aktifleştirildi'); fetchMembers(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Bu üyeyi ve tüm belgelerini silmek istediğinize emin misiniz?')) return;
    try { await api.delete(`/admin/members/${id}`); toast.success('Üye silindi'); fetchMembers(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Silme hatası'); }
  };

  const openEdit = async (m) => {
    try {
      const res = await api.get(`/admin/members/${m.id}`);
      const d = res.data;
      let pdata = d.profile_data || {};
      if (typeof pdata === 'string') pdata = JSON.parse(pdata);
      setEditing(d);
      setEditForm({ first_name: d.first_name || '', last_name: d.last_name || '', email: d.email || '', phone: d.phone || '', is_active: bool(d.is_active), email_verified: bool(d.email_verified), profile_data: pdata });
    } catch { toast.error('Üye bilgileri alınamadı'); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.put(`/admin/members/${editing.id}`, editForm);
      toast.success('Üye güncellendi');
      setEditing(null); setEditForm(null);
      fetchMembers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Güncelleme hatası'); }
    finally { setIsSubmitting(false); }
  };

  const openDocs = async (m) => {
    setDocsMember(m);
    try { const res = await api.get(`/admin/members/${m.id}/documents`); setDocs(res.data); }
    catch { toast.error('Belgeler alınamadı'); }
  };

  const review = async (docId, status) => {
    try {
      await api.post(`/admin/member-documents/${docId}/review`, { status, note: reviewNotes[docId] || '' });
      toast.success('Belge durumu güncellendi');
      const res = await api.get(`/admin/members/${docsMember.id}/documents`);
      setDocs(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get('/admin/members/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'uyeler.csv'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Dışa aktarma başarısız'); }
  };

  const setProfileField = (key, val) => setEditForm(f => ({ ...f, profile_data: { ...f.profile_data, [key]: val } }));

  return (
    <div data-testid="admin-member-manager">
      {/* Ana sekmeler */}
      <div className="mb-6 flex flex-wrap gap-1 border-b border-border">
        {[['members', 'Üyeler'], ['fields', 'Üye Alanları'], ...(documentsEnabled ? [['documents', 'Belge Yönetimi']] : [])].map(([id, label]) => (
          <button key={id} type="button" onClick={() => setMainTab(id)} data-testid={`member-tab-${id}`}
            className={`px-4 py-2.5 -mb-px text-sm font-medium border-b-2 transition-colors duration-200 ${mainTab === id ? 'border-[hsl(var(--gold))] text-[hsl(var(--gold))]' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {label}
          </button>
        ))}
      </div>

      {mainTab === 'fields' && <MemberFieldManager />}
      {mainTab === 'documents' && documentsEnabled && <DocumentManager />}

      {mainTab === 'members' && (
      <>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Üyeler</h1>
          <p className="text-sm text-muted-foreground mt-1">Toplam {data.total} üye</p>
        </div>
        <button onClick={exportCsv} data-testid="export-csv"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-border text-sm font-medium hover:bg-secondary">
          <Download className="w-4 h-4" /> CSV İndir
        </button>
      </div>

      {/* Filters */}
      <div className="mb-5 p-4 rounded-2xl bg-card border border-border grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
        <div className="relative lg:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={filters.search} onChange={e => applyFilter('search', e.target.value)} placeholder="Ad, e-posta, telefon ara..." className={`${inputCls} w-full pl-9`} data-testid="member-search" />
        </div>
        <select value={filters.is_active} onChange={e => applyFilter('is_active', e.target.value)} className={inputCls} data-testid="filter-active">
          <option value="">Tüm Durumlar</option>
          <option value="true">Aktif</option>
          <option value="false">Pasif</option>
        </select>
        <select value={filters.verified} onChange={e => applyFilter('verified', e.target.value)} className={inputCls} data-testid="filter-verified">
          <option value="">Doğrulama (Tümü)</option>
          <option value="true">Doğrulanmış</option>
          <option value="false">Doğrulanmamış</option>
        </select>
        {documentsEnabled && (
          <select value={filters.doc_status} onChange={e => applyFilter('doc_status', e.target.value)} className={inputCls} data-testid="filter-doc-status">
            <option value="">Belge (Tümü)</option>
            <option value="pending">Belge onayı bekleyenler</option>
          </select>
        )}
        <input type="date" value={filters.date_from} onChange={e => applyFilter('date_from', e.target.value)} className={inputCls} title="Başlangıç tarihi" />
        <input type="date" value={filters.date_to} onChange={e => applyFilter('date_to', e.target.value)} className={inputCls} title="Bitiş tarihi" />
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>
      ) : data.items.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground rounded-2xl bg-card border border-border">Kayıtlı üye bulunamadı</div>
      ) : (
        <div className="rounded-2xl bg-card border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3 font-medium">Ad Soyad</th>
                <th className="px-4 py-3 font-medium">E-posta</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">Telefon</th>
                <th className="px-4 py-3 font-medium">Durum</th>
                <th className="px-4 py-3 font-medium hidden lg:table-cell">Kayıt</th>
                <th className="px-4 py-3 font-medium text-right">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(m => (
                <tr key={m.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid="member-row">
                  <td className="px-4 py-3 font-medium">{m.first_name} {m.last_name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{m.email}</td>
                  <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{m.phone || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex w-fit px-2 py-0.5 text-xs rounded-full ${bool(m.is_active) ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>{bool(m.is_active) ? 'Aktif' : 'Pasif'}</span>
                      {!bool(m.email_verified) && <span className="inline-flex w-fit px-2 py-0.5 text-[10px] rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400">Doğrulanmamış</span>}
                      {Number(m.pending_docs) > 0 && <span className="inline-flex items-center gap-1 w-fit px-2 py-0.5 text-[10px] rounded-full bg-orange-500/15 text-orange-600 dark:text-orange-400" data-testid="member-pending-docs-badge"><FileText className="w-3 h-3" /> Belge Onayı Bekliyor</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell">{m.created_at ? new Date(m.created_at).toLocaleDateString('tr-TR') : '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => toggleActive(m)} className="p-2 rounded-lg hover:bg-secondary" title={bool(m.is_active) ? 'Pasifleştir' : 'Aktifleştir'} data-testid="member-toggle-active"><Power className={`w-4 h-4 ${bool(m.is_active) ? 'text-green-600' : 'text-muted-foreground'}`} /></button>
                      <button onClick={() => openDocs(m)} className={`relative p-2 rounded-lg hover:bg-secondary ${Number(m.pending_docs) > 0 ? 'text-orange-500' : ''}`} title={Number(m.pending_docs) > 0 ? `${m.pending_docs} belge onay bekliyor` : 'Belgeler'} data-testid="member-docs"><FileText className="w-4 h-4" />{Number(m.pending_docs) > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[9px] font-bold rounded-full bg-orange-500 text-white">{m.pending_docs}</span>}</button>
                      <button onClick={() => openEdit(m)} className="p-2 rounded-lg hover:bg-secondary" title="Düzenle" data-testid="member-edit"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(m.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive" title="Sil" data-testid="member-delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {data.total_pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-muted-foreground">Sayfa {data.page} / {data.total_pages}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-border text-sm disabled:opacity-40 hover:bg-secondary"><ChevronLeft className="w-4 h-4" /> Önceki</button>
            <button disabled={page >= data.total_pages} onClick={() => setPage(p => p + 1)} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-border text-sm disabled:opacity-40 hover:bg-secondary">Sonraki <ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editing && editForm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => { setEditing(null); setEditForm(null); }}>
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-card border border-border p-6" onClick={e => e.stopPropagation()} data-testid="member-edit-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold">Üye Düzenle</h3>
              <button onClick={() => { setEditing(null); setEditForm(null); }} className="p-1.5 rounded-lg hover:bg-secondary"><XIcon className="w-5 h-5" /></button>
            </div>
            <form onSubmit={saveEdit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1.5">Ad</label><input value={editForm.first_name} onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))} className={`${inputCls} w-full`} required /></div>
                <div><label className="block text-sm font-medium mb-1.5">Soyad</label><input value={editForm.last_name} onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))} className={`${inputCls} w-full`} required /></div>
                <div><label className="block text-sm font-medium mb-1.5">E-posta</label><input type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} className={`${inputCls} w-full`} required /></div>
                <div><label className="block text-sm font-medium mb-1.5">Telefon</label><input value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} className={`${inputCls} w-full`} /></div>
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editForm.is_active} onChange={e => setEditForm(f => ({ ...f, is_active: e.target.checked }))} className="accent-[hsl(var(--gold))]" /> Aktif</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editForm.email_verified} onChange={e => setEditForm(f => ({ ...f, email_verified: e.target.checked }))} className="accent-[hsl(var(--gold))]" /> E-posta Doğrulanmış</label>
              </div>
              {fields.length > 0 && (
                <div className="border-t border-border pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {fields.map(f => (
                    <div key={f.field_key} className={f.field_type === 'textarea' || f.field_type === 'image' ? 'sm:col-span-2' : ''}>
                      <label className="block text-sm font-medium mb-1.5">{f.label}</label>
                      {f.field_type === 'image' ? (
                        <div className="flex items-center gap-3">
                          {editForm.profile_data[f.field_key] && <img src={getFileUrl(editForm.profile_data[f.field_key])} alt="" className="w-12 h-12 rounded-lg object-cover border border-border" />}
                          <span className="text-xs text-muted-foreground">{editForm.profile_data[f.field_key] ? 'Yüklü görsel' : 'Görsel yok'}</span>
                        </div>
                      ) : f.field_type === 'select' ? (
                        <select value={editForm.profile_data[f.field_key] || ''} onChange={e => setProfileField(f.field_key, e.target.value)} className={`${inputCls} w-full`}>
                          <option value="">Seçiniz</option>
                          {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : f.field_type === 'textarea' ? (
                        <textarea value={editForm.profile_data[f.field_key] || ''} onChange={e => setProfileField(f.field_key, e.target.value)} rows={2} className={`${inputCls} w-full`} />
                      ) : (
                        <input type={f.field_type === 'date' ? 'date' : 'text'} value={editForm.profile_data[f.field_key] || ''} onChange={e => setProfileField(f.field_key, e.target.value)} className={`${inputCls} w-full`} />
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={isSubmitting} className="px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium disabled:opacity-50" data-testid="member-edit-save">{isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}</button>
                <button type="button" onClick={() => { setEditing(null); setEditForm(null); }} className="px-6 py-2.5 rounded-lg border border-input text-sm hover:bg-secondary">İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Documents Modal */}
      {docsMember && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setDocsMember(null)}>
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-card border border-border p-6" onClick={e => e.stopPropagation()} data-testid="member-docs-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading text-lg font-semibold">Belgeler</h3>
                <p className="text-sm text-muted-foreground">{docsMember.first_name} {docsMember.last_name}</p>
              </div>
              <button onClick={() => setDocsMember(null)} className="p-1.5 rounded-lg hover:bg-secondary"><XIcon className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              {docs.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Tanımlı belge yok</p>}
              {docs.map(d => {
                const up = d.upload;
                const st = up ? DOC_STATUS[up.status] : null;
                const StIcon = st?.icon;
                return (
                  <div key={d.id} className="p-4 rounded-xl border border-border" data-testid="admin-doc-item">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{d.name}</span>
                          {bool(d.is_required) && <span className="text-[10px] px-1.5 py-0.5 rounded bg-destructive/10 text-destructive">Zorunlu</span>}
                        </div>
                        {st && <span className={`inline-flex items-center gap-1 mt-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${st.cls}`}>{StIcon && <StIcon className="w-3 h-3" />} {st.label}</span>}
                        {up?.reviewed_by && <p className="text-[11px] text-muted-foreground mt-1">İnceleyen: {up.reviewed_by}</p>}
                      </div>
                      {up?.file_path ? (
                        <a href={getFileUrl(up.file_path)} target="_blank" rel="noreferrer" className="text-xs text-[hsl(var(--gold))] hover:underline shrink-0">Görüntüle</a>
                      ) : <span className="text-xs text-muted-foreground shrink-0">Yüklenmedi</span>}
                    </div>
                    {up && (
                      <div className="mt-3 space-y-2">
                        <input value={reviewNotes[up.id] || ''} onChange={e => setReviewNotes(n => ({ ...n, [up.id]: e.target.value }))} placeholder="İnceleme notu (red/yeniden istek için)" className={`${inputCls} w-full text-xs`} />
                        <div className="flex gap-2">
                          <button onClick={() => review(up.id, 'approved')} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-medium hover:bg-emerald-500/20"><Check className="w-3.5 h-3.5" /> Onayla</button>
                          <button onClick={() => review(up.id, 'rejected')} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-destructive/10 text-destructive text-xs font-medium hover:bg-destructive/20"><XIcon className="w-3.5 h-3.5" /> Reddet</button>
                          <button onClick={() => review(up.id, 're_requested')} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-medium hover:bg-amber-500/20"><AlertCircle className="w-3.5 h-3.5" /> Yeniden İste</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
